// ResetPasswordPage.js
import React, { useState, useEffect } from 'react';
import {
  Button,
  TextInput,
  Form,
  FormGroup,
  ProgressIndicator,
  ProgressStep,
  InlineLoading,
} from '@carbon/react';
import { useNavigate } from 'react-router-dom';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const steps = ['Enter Email', 'Verify Code', 'New Password'];

  const handleSendCode = async e => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      // Simulate API call to send reset code
      const resp = await fetch('/mlapi/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!resp.ok) throw new Error('Failed to send reset code');
      setCurrentStep(1);
    } catch (err) {
      setErrorMessage(err.message || 'Error sending reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async e => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      // Simulate API call to verify code
      const resp = await fetch('/mlapi/auth/verify-reset-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: resetCode }),
      });

      if (!resp.ok) throw new Error('Invalid verification code');
      setCurrentStep(2);
    } catch (err) {
      setErrorMessage(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async e => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // Simulate API call to reset password
      const resp = await fetch('/mlapi/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          code: resetCode,
          newPassword,
        }),
      });

      if (!resp.ok) throw new Error('Password reset failed');

      // Redirect to login with success message
      navigate('/login', {
        state: {
          successMessage:
            'Password reset successfully! Please login with your new password.',
        },
      });
    } catch (err) {
      setErrorMessage(err.message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Form onSubmit={handleSendCode}>
            <FormGroup legendText="Enter your email to reset password">
              <TextInput
                id="resetEmail"
                labelText="Email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </FormGroup>
            {renderMessages()}
            <div className="action-buttons">
              <Button kind="secondary" onClick={() => navigate('/login')}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <InlineLoading /> : 'Send Reset Code'}
              </Button>
            </div>
          </Form>
        );

      case 1:
        return (
          <Form onSubmit={handleVerifyCode}>
            <FormGroup legendText="Verify Reset Code">
              <p>Check your email for a reset code</p>
              <TextInput
                id="resetCode"
                labelText="Verification Code"
                placeholder="Enter 6-digit code"
                value={resetCode}
                onChange={e => setResetCode(e.target.value)}
                required
              />
            </FormGroup>
            {renderMessages()}
            <div className="action-buttons">
              <Button kind="secondary" onClick={() => setCurrentStep(0)}>
                Back
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <InlineLoading /> : 'Verify Code'}
              </Button>
            </div>
          </Form>
        );

      case 2:
        return (
          <Form onSubmit={handleResetPassword}>
            <FormGroup legendText="Set New Password">
              <TextInput
                id="newPassword"
                labelText="New Password"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
              />
              <TextInput
                id="confirmPassword"
                labelText="Confirm Password"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
              />
            </FormGroup>
            {renderMessages()}
            <div className="action-buttons">
              <Button kind="secondary" onClick={() => setCurrentStep(1)}>
                Back
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <InlineLoading /> : 'Reset Password'}
              </Button>
            </div>
          </Form>
        );

      default:
        return null;
    }
  };

  const renderMessages = () => (
    <>
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
    </>
  );

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <ProgressIndicator currentIndex={currentStep}>
          {steps.map(s => (
            <ProgressStep label={s} key={s} />
          ))}
        </ProgressIndicator>
        <div className="step-content">
          <h3>{steps[currentStep]}</h3>
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
